import React from 'react';
import { formatUrl } from '@plone/volto/helpers/Api/Api';

import { FEEDBACK_COPY, PAGE_FEEDBACK_ENDPOINT } from './constants';
import type { FeedbackFormValues } from './feedbackSchema';

export type FeedbackStatus = 'idle' | 'success' | 'error';

export interface FeedbackSubmitResult {
  status: FeedbackStatus;
  /** Erros por campo devolvidos pelo backend (HTTP 400). */
  fieldErrors?: Partial<Record<keyof FeedbackFormValues, string>>;
  /** Mensagem de alerta a mostrar acima do formulário, sem trocar de tela. */
  alert?: string;
}

interface FeedbackPayload extends FeedbackFormValues {
  page_path: string;
  referrer: string;
  /** Honeypot: bot preenche, humano não vê. O backend descarta silenciosamente. */
  website: string;
}

const buildPayload = (
  values: FeedbackFormValues,
  pagePath: string,
): FeedbackPayload => ({
  ...values,
  page_path: pagePath,
  referrer: typeof document === 'undefined' ? '' : document.referrer,
  website: '',
});

/**
 * Envia o feedback da 404 para o `@page-feedback`.
 *
 * Segue o caminho de escrita que já existe no projeto (`Blocks/Poll/View.tsx`):
 * `fetch` + `formatUrl`, com o tratamento decidido pelo status HTTP em vez de
 * inspecionar o corpo. Sem action/reducer porque não há nada para guardar no
 * store — o resultado vive e morre dentro do modal.
 */
export function useNotFoundFeedback(pagePath: string) {
  const [status, setStatus] = React.useState<FeedbackStatus>('idle');
  const [errorMessage, setErrorMessage] = React.useState<string>('');

  const submit = React.useCallback(
    async (values: FeedbackFormValues): Promise<FeedbackSubmitResult> => {
      try {
        const response = await fetch(formatUrl(PAGE_FEEDBACK_ENDPOINT), {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(buildPayload(values, pagePath)),
        });

        if (response.ok) {
          setStatus('success');
          return { status: 'success' };
        }

        // 400: o backend valida de novo (o cliente é sugestão, não garantia)
        // e devolve `fields` no mesmo formato dos erros do zod.
        if (response.status === 400) {
          const body = await response.json().catch(() => ({}));
          return {
            status: 'idle',
            fieldErrors: body?.fields,
            alert: FEEDBACK_COPY.invalidForm,
          };
        }

        if (response.status === 429) {
          return { status: 'idle', alert: FEEDBACK_COPY.rateLimited };
        }

        setErrorMessage(FEEDBACK_COPY.genericError);
        setStatus('error');
        return { status: 'error' };
      } catch {
        // Rede fora do ar / requisição abortada: mesma tela de erro, já que
        // do ponto de vista do cidadão o resultado é o mesmo.
        setErrorMessage(FEEDBACK_COPY.genericError);
        setStatus('error');
        return { status: 'error' };
      }
    },
    [pagePath],
  );

  const backToForm = React.useCallback(() => {
    setStatus('idle');
    setErrorMessage('');
  }, []);

  return { status, errorMessage, submit, backToForm };
}

export default useNotFoundFeedback;
